const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const guestNames = [
    "Ava Patel",
    "Lucas Martin",
    "Sofia Nguyen",
    "Daniel Kim",
    "Emma Costa",
    "Noah Hassan",
    "Priya Shah",
    "Liam Brown",
    "Chloe Davis",
    "Mason Lee",
    "Zoe Johnson",
    "Ethan Rivera",
    "Mila Chen",
    "Jacob Lewis",
    "Harper White",
];

const randomFrom = (values, seed) => values[seed % values.length];

const formatDateKey = (date) => date.toISOString().slice(0, 10);

const buildTransaction = ({ date, index, type, channel, status, durationSec, guest }) => ({
    id: `${type.toUpperCase().slice(0, 3)}-${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}-${String(index).padStart(3, "0")}`,
    date: formatDateKey(date),
    timestamp: new Date(date).toISOString(),
    type,
    channel,
    status,
    durationSec,
    guest,
    room: `R${String((index * 7) % 240 + 101).padStart(3, "0")}`,
    amount: Number((durationSec * 0.12).toFixed(2)),
    source: randomFrom(["Front Desk", "Kiosk", "Mobile", "Walk-in Desk"], index),
    tier: randomFrom(["Standard", "Extended", "VIP"], index + 5),
});

export const generatePerformanceTransactions = (range = "6m") => {
    const today = new Date();
    let lookbackMonths = 6;

    if (range === "7d") lookbackMonths = 0.25;
    if (range === "1m") lookbackMonths = 1;
    if (range === "3m") lookbackMonths = 3;

    const startDate = new Date(today);
    startDate.setHours(0, 0, 0, 0);

    if (range === "7d") {
        startDate.setDate(today.getDate() - 6);
    } else {
        startDate.setMonth(today.getMonth() - lookbackMonths);
    }

    const records = [];
    const cursor = new Date(startDate);

    while (cursor <= today) {
        const dayFactor = 1 + Math.sin(cursor.getDate() / 3.1) * 0.35;
        const weekdayBoost = [1.05, 1.1, 1.18, 1.2, 1.28, 1.24, 1.08][cursor.getDay()];
        const totalCheckins = Math.max(8, Math.round(24 * dayFactor * weekdayBoost));
        const totalWalkins = Math.max(3, Math.round(8 * dayFactor));

        for (let index = 0; index < totalCheckins; index += 1) {
            const isExtended = (index + cursor.getDate()) % 6 === 0;
            const channel = isExtended ? "extended" : "standard";
            const type = "checkin";
            const durationSec = Math.round(180 + ((index * 19 + cursor.getDate() * 11) % 170) + (isExtended ? 90 : 0));
            const status = index % 13 === 0 ? "pending" : "completed";
            const guest = randomFrom(guestNames, index + cursor.getDate());

            records.push(
                buildTransaction({
                    date: new Date(cursor),
                    index: records.length + 1,
                    type,
                    channel,
                    status,
                    durationSec,
                    guest,
                }),
            );
        }

        for (let index = 0; index < totalWalkins; index += 1) {
            const type = "walkin";
            const channel = index % 5 === 0 ? "extended" : "standard";
            const durationSec = Math.round(220 + ((index * 23 + cursor.getDate() * 13) % 180) + (channel === "extended" ? 110 : 0));
            const status = index % 9 === 0 ? "review" : "completed";
            const guest = randomFrom(guestNames, index + cursor.getDate() + 10);

            records.push(
                buildTransaction({
                    date: new Date(cursor),
                    index: records.length + 1,
                    type,
                    channel,
                    status,
                    durationSec,
                    guest,
                }),
            );
        }

        cursor.setDate(cursor.getDate() + 1);
    }

    return records;
};

export const summarizeTransactions = (transactions, selectedRange, granularity = "monthly") => {
    const seriesMap = new Map();

    transactions.forEach((item) => {
        const dt = new Date(item.date);
        let label = monthNames[dt.getMonth()];

        if (selectedRange === "7d") {
            label = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][dt.getDay() === 0 ? 6 : dt.getDay() - 1];
        } else if (selectedRange === "1m") {
            if (granularity === "daily") {
                label = `D${dt.getDate()}`;
            } else {
                const weekNumber = Math.ceil(dt.getDate() / 7);
                label = `W${weekNumber}`;
            }
        } else if (selectedRange === "3m" || selectedRange === "6m") {
            label = monthNames[dt.getMonth()];
        }

        const entry = seriesMap.get(label) || {
            label,
            checkinStandard: 0,
            walkinStandard: 0,
            checkinExtended: 0,
            walkinExtended: 0,
            checkinTransactions: 0,
            walkinTransactions: 0,
        };

        if (item.type === "checkin") {
            entry.checkinTransactions += 1;
            if (item.channel === "standard") entry.checkinStandard += item.durationSec / 60;
            else entry.checkinExtended += item.durationSec / 60;
        } else {
            entry.walkinTransactions += 1;
            if (item.channel === "standard") entry.walkinStandard += item.durationSec / 60;
            else entry.walkinExtended += item.durationSec / 60;
        }

        seriesMap.set(label, entry);
    });

    const chartData = Array.from(seriesMap.values()).map((item) => ({
        ...item,
        checkinStandard: Number((item.checkinStandard / Math.max(1, item.checkinTransactions || 1)).toFixed(2)),
        walkinStandard: Number((item.walkinStandard / Math.max(1, item.walkinTransactions || 1)).toFixed(2)),
        checkinExtended: Number((item.checkinExtended / Math.max(1, Math.max(1, item.checkinTransactions || 1))).toFixed(2)),
        walkinExtended: Number((item.walkinExtended / Math.max(1, item.walkinTransactions || 1)).toFixed(2)),
    }));

    const checkins = transactions.filter((item) => item.type === "checkin").length;
    const walkins = transactions.filter((item) => item.type === "walkin").length;
    const other = Math.round((checkins + walkins) * 0.12);

    return {
        chartData,
        totals: {
            checkins,
            walkins,
            other,
            avgCheckin: Number((transactions.filter((item) => item.type === "checkin").reduce((sum, item) => sum + item.durationSec, 0) / Math.max(1, checkins)) / 60).toFixed(2),
            avgWalkin: Number((transactions.filter((item) => item.type === "walkin").reduce((sum, item) => sum + item.durationSec, 0) / Math.max(1, walkins)) / 60).toFixed(2),
        },
    };
};

export const getDrilldownTable = (transactions, limit = 8) =>
    transactions
        .slice(0, limit)
        .map((item) => ({
            ...item,
            key: item.id,
            duration: `${String(Math.floor(item.durationSec / 60)).padStart(2, "0")}:${String(item.durationSec % 60).padStart(2, "0")}`,
        }));
